import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import api from "../../utils/api";
import "./EnquiriesCard.css";

// Tutor/centre-facing card for incoming enquiries ("Contact Tutor" /
// "Request Information" clicks). The actual conversation happens off-platform
// (WhatsApp/phone — contact details are shown right here), so this just
// tracks whether the provider followed up and whether it turned into a
// match, which is what the admin-side enquiry analytics are built from.
const STATUS_LABELS = {
  new: "New",
  responded: "Responded",
  matched: "Matched",
  no_response: "No response",
  closed: "Closed",
};

const EnquiriesCard = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/enquiries/mine");
      setEnquiries(res.data.enquiries || []);
    } catch (err) {
      console.error("Failed to load enquiries:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/enquiries/${id}/status`, { status });
      toast.success(`Marked as ${STATUS_LABELS[status].toLowerCase()}`);
      fetchEnquiries();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    } finally {
      setBusyId(null);
    }
  };

  const active = enquiries.filter((e) => !["closed", "no_response"].includes(e.status));

  if (loading) return null;
  if (active.length === 0) return null;

  return (
    <div className="enquiries-card">
      <h3>Enquiries ({active.length})</h3>
      <div className="enquiries-list">
        {active.map((en) => (
          <div key={en._id} className="enquiry-item">
            <div className="enquiry-main">
              <p className="enquiry-name">
                {en.studentName}
                {en.intent === "enrolment" && " — enrolment enquiry"}
              </p>
              {en.message && <p className="enquiry-message">"{en.message}"</p>}
              <div className="enquiry-contact">
                <a href={`mailto:${en.studentEmail}`}>{en.studentEmail}</a>
                {en.studentPhone && (
                  <>
                    {" · "}
                    <a href={`tel:${en.studentPhone}`}>{en.studentPhone}</a>
                  </>
                )}
              </div>
              <span className={`enquiry-status-pill enquiry-status-${en.status}`}>
                {STATUS_LABELS[en.status]}
              </span>
            </div>

            <div className="enquiry-actions">
              {en.status === "new" && (
                <button
                  className="enquiry-btn enquiry-btn-responded"
                  disabled={busyId === en._id}
                  onClick={() => updateStatus(en._id, "responded")}
                >
                  Mark Responded
                </button>
              )}
              {["new", "responded"].includes(en.status) && (
                <button
                  className="enquiry-btn enquiry-btn-matched"
                  disabled={busyId === en._id}
                  onClick={() => updateStatus(en._id, "matched")}
                >
                  Mark Matched
                </button>
              )}
              {en.status !== "closed" && (
                <button
                  className="enquiry-btn enquiry-btn-close"
                  disabled={busyId === en._id}
                  onClick={() => updateStatus(en._id, "closed")}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EnquiriesCard;
